/**
 * FiltersSection - views, busca e filtros da fila de domínios pendentes.
 */
import React, { useMemo, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Typography,
    Chip,
    Button,
    Divider,
    Collapse,
    IconButton,
    Stack,
    Tooltip,
    InputAdornment
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    FilterList as FilterListIcon,
    WarningAmber as WarningAmberIcon,
    AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';

const missingMetadataFilters = [
    { key: 'abrangencia', label: 'Abrangência', color: 'warning' },
    { key: 'categories', label: 'Categoria', color: 'info' },
    { key: 'tipo', label: 'Tipo', color: 'default' },
    { key: 'estado', label: 'Estado', color: 'default' },
    { key: 'cidade', label: 'Cidade', color: 'default' }
];

const advancedFilterOptions = {
    pais: [
        { value: 'BR', label: 'Brasil' },
        { value: 'US', label: 'EUA' },
        { value: 'AR', label: 'Argentina' },
        { value: 'PT', label: 'Portugal' }
    ],
    abrangencia: [
        { value: 'Nacional', label: 'Nacional' },
        { value: 'Regional', label: 'Regional' },
        { value: 'Local', label: 'Local' }
    ],
    tipo: [
        { value: 'Portal', label: 'Portal' },
        { value: 'Jornal', label: 'Jornal' },
        { value: 'Revista', label: 'Revista' },
        { value: 'Blog', label: 'Blog' },
        { value: 'Site', label: 'Site' },
        { value: 'Governo', label: 'Governo' }
    ],
    categories: [
        { value: 'Agronegócio', label: 'Agronegócio' },
        { value: 'Tecnologia', label: 'Tecnologia' },
        { value: 'Saúde', label: 'Saúde' },
        { value: 'Educação', label: 'Educação' },
        { value: 'Economia', label: 'Economia' },
        { value: 'Política', label: 'Política' },
        { value: 'Esportes', label: 'Esportes' },
        { value: 'Entretenimento', label: 'Entretenimento' },
        { value: 'Geral', label: 'Geral' }
    ],
    estado: [
        { value: 'SP', label: 'São Paulo' },
        { value: 'RJ', label: 'Rio de Janeiro' },
        { value: 'MG', label: 'Minas Gerais' },
        { value: 'RS', label: 'Rio Grande do Sul' },
        { value: 'PR', label: 'Paraná' },
        { value: 'SC', label: 'Santa Catarina' },
        { value: 'BA', label: 'Bahia' },
        { value: 'PE', label: 'Pernambuco' },
        { value: 'CE', label: 'Ceará' },
        { value: 'DF', label: 'Distrito Federal' }
    ]
};

const FiltersSection = ({
    searchInput,
    onSearchChange,
    processedAtFilter,
    setProcessedAtFilter,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    showCompleted,
    setShowCompleted,
    enrichedByFilter,
    setEnrichedByFilter,
    advancedFilters,
    setAdvancedFilters,
    views = [],
    viewCounts = {},
    selectedView,
    onViewChange,
    audienceSourceFilter,
    showSearch = true,
    setAudienceSourceFilter
}) => {
    const theme = useTheme();
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const missingData = advancedFilters?.missingData || [];
    const hasMissingFilter = missingData.length > 0;
    const selectedViewData = views.find((view) => view.id === selectedView);
    const hasPrimaryFilterRow = showSearch || !selectedView;

    const hasAdvancedMetadataFilters = useMemo(() => (
        Object.entries(advancedFilters || {}).some(([key, value]) => {
            if (key === 'missingData') return false;
            return Array.isArray(value) ? value.length > 0 : Boolean(value);
        })
    ), [advancedFilters]);

    const activeFilterCount = [
        selectedView,
        searchInput,
        statusFilter && statusFilter !== 'all',
        priorityFilter,
        enrichedByFilter && enrichedByFilter !== 'all',
        audienceSourceFilter && audienceSourceFilter !== 'all',
        processedAtFilter && processedAtFilter !== 'all',
        hasMissingFilter,
        hasAdvancedMetadataFilters
    ].filter(Boolean).length;

    const hasAnyFilter =
        activeFilterCount > 0 ||
        Object.values(advancedFilters || {}).some(v => Array.isArray(v) ? v.length > 0 : v);

    const toggleMissing = (key) => {
        const next = missingData.includes(key)
            ? missingData.filter(k => k !== key)
            : [...missingData, key];
        setAdvancedFilters({ ...advancedFilters, missingData: next });
    };

    const clearAllFilters = () => {
        setStatusFilter('all');
        setPriorityFilter('');
        setEnrichedByFilter('all');
        setAudienceSourceFilter('all');
        setProcessedAtFilter('all');
        setAdvancedFilters({});
        onSearchChange('');
        onViewChange(null);
    };

    const selectSx = {
        minWidth: 0,
        '& .MuiSelect-select': {
            py: 1.1
        }
    };

    const primaryFilterGridColumns = showSearch
        ? {
            xs: '1fr',
            md: selectedView
                ? 'minmax(280px, 440px)'
                : 'minmax(280px, 2fr) repeat(2, minmax(150px, 1fr))',
            xl: selectedView
                ? 'minmax(320px, 480px)'
                : 'minmax(300px, 2fr) minmax(132px, 0.9fr) minmax(150px, 1fr) minmax(164px, 1.1fr) minmax(160px, 1.05fr) minmax(146px, 0.95fr) auto'
        }
        : {
            xs: '1fr',
            sm: 'repeat(2, minmax(150px, 1fr))',
            lg: 'repeat(3, minmax(150px, 1fr))',
            xl: 'minmax(132px, 0.9fr) minmax(150px, 1fr) minmax(164px, 1.1fr) minmax(160px, 1.05fr) minmax(146px, 0.95fr) auto'
        };

    const filterTop = {
        xs: 'calc(var(--admin-appbar-height, 64px) + 198px)',
        sm: 'calc(var(--admin-appbar-height, 64px) + 152px)',
        md: 'calc(var(--admin-appbar-height, 64px) + 132px)',
        xl: 'calc(var(--admin-appbar-height, 64px) + 92px)'
    };

    const filterMaxHeight = {
        xs: 'calc(100vh - (var(--admin-appbar-height, 64px) + 216px))',
        sm: 'calc(100vh - (var(--admin-appbar-height, 64px) + 170px))',
        md: 'calc(100vh - (var(--admin-appbar-height, 64px) + 150px))',
        xl: 'calc(100vh - (var(--admin-appbar-height, 64px) + 110px))'
    };

    const renderAdvancedSelect = (key, label, minWidth = 132) => (
        <FormControl size="small" sx={{ ...selectSx, minWidth }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={advancedFilters?.[key] || ''}
                onChange={(e) => setAdvancedFilters({ ...advancedFilters, [key]: e.target.value })}
                label={label}
            >
                <MenuItem value="">Todos</MenuItem>
                {advancedFilterOptions[key].map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <Card
            elevation={0}
            sx={{
                position: 'fixed',
                top: filterTop,
                left: { xs: 12, md: 'calc(var(--admin-sidebar-width, 240px) + 24px)' },
                right: { xs: 12, md: 24 },
                zIndex: theme.zIndex.appBar,
                mb: 0,
                background: theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : theme.palette.background.paper,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.08)
                    : alpha(theme.palette.common.black, 0.08),
                borderRadius: 2,
                maxHeight: filtersOpen ? filterMaxHeight : { xs: 92, md: 78 },
                overflowY: filtersOpen ? 'auto' : 'hidden',
                overflowX: 'hidden',
                transition: 'max-height 180ms ease, box-shadow 180ms ease',
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 14px 34px rgba(0, 0, 0, 0.30)'
                    : '0 14px 34px rgba(15, 23, 42, 0.10)',
                '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.18)
                        : alpha(theme.palette.common.white, 0.72)
                }
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        mb: 1.5,
                        flexDirection: { xs: 'column', md: 'row' }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: 'primary.main',
                                flexShrink: 0
                            }}
                        >
                            <FilterListIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" fontWeight={800}>
                                    Filtros da fila
                                </Typography>
                                {activeFilterCount > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${activeFilterCount} ativo${activeFilterCount > 1 ? 's' : ''}`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 22, fontWeight: 700 }}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {selectedViewData ? `View ativa: ${selectedViewData.name}` : 'Busca, status, prioridade e metadados em uma ordem única.'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {hasAnyFilter && (
                            <Tooltip title="Limpar busca, views e filtros manuais">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<ClearIcon />}
                                    onClick={clearAllFilters}
                                    sx={{ textTransform: 'none', borderRadius: 1.5, whiteSpace: 'nowrap' }}
                                >
                                    Limpar filtros
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            size="small"
                            variant={filtersOpen ? 'outlined' : 'contained'}
                            startIcon={<FilterListIcon />}
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: '0.2s',
                                        fontSize: 17
                                    }}
                                />
                            }
                            onClick={() => setFiltersOpen((current) => !current)}
                            sx={{ textTransform: 'none', borderRadius: 1.5, whiteSpace: 'nowrap', fontWeight: 700 }}
                        >
                            {filtersOpen ? 'Guardar filtros' : 'Abrir filtros'}
                        </Button>
                    </Box>
                </Box>

                <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                {views.length > 0 && (
                    <Box sx={{ mb: 1.75 }}>
                        <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                                overflowX: 'auto',
                                pb: 0.5,
                                '&::-webkit-scrollbar': { height: 6 },
                                '&::-webkit-scrollbar-thumb': {
                                    borderRadius: 999,
                                    bgcolor: alpha(theme.palette.text.primary, 0.16)
                                }
                            }}
                        >
                            <Chip
                                label="Todos"
                                size="small"
                                onClick={() => onViewChange(null)}
                                color={!selectedView ? 'primary' : 'default'}
                                variant={!selectedView ? 'filled' : 'outlined'}
                                sx={{ fontWeight: !selectedView ? 700 : 500, cursor: 'pointer', flexShrink: 0 }}
                            />
                            {views.map((view) => {
                                const count = viewCounts[view.id] || 0;
                                const isActive = selectedView === view.id;
                                return (
                                    <Chip
                                        key={view.id}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>{view.icon}</span>
                                                <span>{view.name}</span>
                                                {count > 0 && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ml: 0.25,
                                                            px: 0.65,
                                                            py: 0.1,
                                                            borderRadius: 999,
                                                            bgcolor: isActive
                                                                ? alpha(theme.palette.common.white, 0.22)
                                                                : alpha(theme.palette.text.primary, 0.10),
                                                            fontSize: '0.72rem',
                                                            fontWeight: 800,
                                                            lineHeight: 1.3
                                                        }}
                                                    >
                                                        {count.toLocaleString('pt-BR')}
                                                    </Box>
                                                )}
                                            </Box>
                                        }
                                        size="small"
                                        onClick={() => onViewChange(view.id)}
                                        color={isActive ? 'primary' : 'default'}
                                        variant={isActive ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: isActive ? 700 : 500, cursor: 'pointer', flexShrink: 0 }}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                )}

                {hasPrimaryFilterRow && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: primaryFilterGridColumns,
                        gap: 1.25,
                        alignItems: 'center'
                    }}
                >
                    {showSearch && (
                    <TextField
                        size="small"
                        placeholder={selectedViewData
                            ? `Buscar em "${selectedViewData.name}"...`
                            : 'Buscar domínio ou site...'}
                        value={searchInput}
                        onChange={(e) => onSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchInput ? (
                                <IconButton size="small" onClick={() => onSearchChange('')} edge="end">
                                    <ClearIcon sx={{ fontSize: '1.05rem' }} />
                                </IconButton>
                            ) : null
                        }}
                    />
                    )}

                    {!selectedView && (
                        <>
                            <FormControl size="small" sx={selectSx}>
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                                    <MenuItem value="all">Todos</MenuItem>
                                    <MenuItem value="pending">Pendente</MenuItem>
                                    <MenuItem value="processing">Processando</MenuItem>
                                    <Divider />
                                    <MenuItem value="completed">Concluído (todos)</MenuItem>
                                    <MenuItem value="completed:estimated">Concluído (estimado)</MenuItem>
                                    <MenuItem value="completed:partial">Concluído (parcial)</MenuItem>
                                    <MenuItem value="completed:actual">Concluído (real)</MenuItem>
                                    <MenuItem value="ignored">Ignorado</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={selectSx}>
                                <InputLabel>Prioridade</InputLabel>
                                <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} label="Prioridade">
                                    <MenuItem value="">Todas</MenuItem>
                                    <MenuItem value="1">P1 - Scraping urgente</MenuItem>
                                    <MenuItem value="2">P2 - Alta</MenuItem>
                                    <MenuItem value="3">P3 - Normal</MenuItem>
                                    <MenuItem value="4">P4 - Baixa</MenuItem>
                                    <MenuItem value="5">P5 - Mínima</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={selectSx}>
                                <InputLabel>Enriquecimento</InputLabel>
                                <Select value={enrichedByFilter || 'all'} onChange={(e) => setEnrichedByFilter(e.target.value)} label="Enriquecimento">
                                    <MenuItem value="all">Todos</MenuItem>
                                    <Divider />
                                    <MenuItem value="gemini">Gemini AI</MenuItem>
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="auto-enrich">Auto-Enrich</MenuItem>
                                    <MenuItem value="audience-only">Apenas audiência</MenuItem>
                                    <Divider />
                                    <MenuItem value="none">Não enriquecido</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={selectSx}>
                                <InputLabel>Audiência</InputLabel>
                                <Select value={audienceSourceFilter || 'all'} onChange={(e) => setAudienceSourceFilter(e.target.value)} label="Audiência">
                                    <MenuItem value="all">Todas</MenuItem>
                                    <Divider />
                                    <MenuItem value="similarweb">SimilarWeb</MenuItem>
                                    <MenuItem value="semrush">Semrush</MenuItem>
                                    <MenuItem value="hypestat">HypeStat</MenuItem>
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="fallback">Fallback</MenuItem>
                                    <MenuItem value="unknown">Desconhecido</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={selectSx}>
                                <InputLabel>Período</InputLabel>
                                <Select value={processedAtFilter || 'all'} onChange={(e) => setProcessedAtFilter(e.target.value)} label="Período">
                                    <MenuItem value="all">Todo período</MenuItem>
                                    <MenuItem value="24h">Últimas 24h</MenuItem>
                                    <MenuItem value="7d">Últimos 7 dias</MenuItem>
                                    <MenuItem value="30d">Últimos 30 dias</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={<Switch size="small" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
                                label={<Typography variant="body2" color="text.secondary">Processados</Typography>}
                                sx={{ ml: 0, mr: 0, justifySelf: { xl: 'end' }, whiteSpace: 'nowrap' }}
                            />
                        </>
                    )}
                </Box>
                )}

                {!selectedView && (
                    <>
                        <Divider sx={{ my: 1.75 }} />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: '1fr auto' },
                                gap: 1.5,
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.5, mr: 0.25 }}>
                                    Sem metadados
                                </Typography>
                                {missingMetadataFilters.map(({ key, label, color }) => {
                                    const isActive = missingData.includes(key);
                                    return (
                                        <Chip
                                            key={key}
                                            size="small"
                                            icon={<WarningAmberIcon sx={{ fontSize: '0.95rem !important' }} />}
                                            label={label}
                                            color={isActive ? color : 'default'}
                                            variant={isActive ? 'filled' : 'outlined'}
                                            onClick={() => toggleMissing(key)}
                                            onDelete={isActive ? () => toggleMissing(key) : undefined}
                                            sx={{
                                                cursor: 'pointer',
                                                fontWeight: isActive ? 700 : 500,
                                                flexShrink: 0
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            <Button
                                size="small"
                                variant={showAdvancedFilters || hasAdvancedMetadataFilters ? 'outlined' : 'text'}
                                startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                                endIcon={
                                    <ExpandMoreIcon
                                        sx={{
                                            transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: '0.2s',
                                            fontSize: 17
                                        }}
                                    />
                                }
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                sx={{
                                    textTransform: 'none',
                                    justifySelf: { xs: 'start', lg: 'end' },
                                    borderRadius: 1.5,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Filtros Gemini
                            </Button>
                        </Box>

                        <Collapse in={showAdvancedFilters}>
                            <Box
                                sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.04)
                                        : alpha(theme.palette.common.black, 0.025)
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, minmax(140px, 1fr))',
                                            lg: 'repeat(7, minmax(120px, 1fr))'
                                        },
                                        gap: 1.25,
                                        alignItems: 'center'
                                    }}
                                >
                                    {renderAdvancedSelect('pais', 'País', 120)}
                                    {renderAdvancedSelect('abrangencia', 'Abrangência', 136)}
                                    {renderAdvancedSelect('tipo', 'Tipo', 126)}
                                    {renderAdvancedSelect('categories', 'Categoria', 142)}
                                    {renderAdvancedSelect('estado', 'Estado', 118)}
                                    <TextField
                                        size="small"
                                        label="Cidade"
                                        value={advancedFilters?.cidade || ''}
                                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, cidade: e.target.value })}
                                        placeholder="Ex: São Paulo"
                                    />
                                    <TextField
                                        size="small"
                                        label="Sede"
                                        value={advancedFilters?.sede || ''}
                                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, sede: e.target.value })}
                                        placeholder="Ex: Brasília"
                                    />
                                </Box>

                                {hasAdvancedMetadataFilters && (
                                    <Box sx={{ mt: 1.25 }}>
                                        <Button
                                            size="small"
                                            startIcon={<ClearIcon />}
                                            onClick={() => setAdvancedFilters({ missingData })}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Limpar Gemini
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                    </>
                )}
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default FiltersSection;
